import {
  BuildOutlined,
  ChangeCircle,
  FactoryOutlined,
  Group,
  ScheduleOutlined,
  WorkHistory
} from "@mui/icons-material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {
	Box,
	Chip,
	ListItemText,
	MenuItem,
	MenuList,
	Popover,
	Tooltip,
	Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { isPast } from "date-fns";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { FULL_TIME_SCHEDULER_PERMISSIONS } from "../../../../config/permissions";
import { formatDate, hasPermissionString } from "../../../../utils/helpers";
import { StoreType } from "../../../../utils/types";
import { NON_MOVABLE_STATUS_CODES} from "../../../full-time-shifts/ui/weekly-shift-view/constants";
import useWorkerSchedulerData from "../../controllers/use-worker-scheduler-data";
//import { schedulingStatusesObj } from "../../data";
import { openTransitionDialog } from "../../store/worker-scheduler-slice";
import { getTransition } from "../../utils/transition-config";
import { useFilteredStatusOptions } from "../status-transition-handler";

interface FTWorkerViewCardProps {
	startDateTime: string;
	endDateTime: string;
	shiftWorkcenter: number;
	shiftPrimaryJob: number;
	isOverTimeShift?: boolean;
	isFlexShift?: boolean;
	isSameWorkcenter?: boolean;
	isSamePrimaryJob?: boolean;
  isSameShiftPattern?: boolean;
	schedulingStatus?: string;
	previousSchedulingStatus?: string;
	workerId?: string;
	shiftId?: string;
	workerFullName?: string;
	shiftPatternName?: string;
	workerJobWorkcenter?: {
		jobId: number;
		workCenterId: number;
		jobName: string;
		workCenterName: string;
	}[];
	isAttendanceManagementEnabled: boolean;
  workerPrimaryWorkcenterName?: string;
  workerPrimaryJobName?: string;
  isPrinting?: boolean;
  skillIds?: string;
  isMoved?: boolean;
}

const FTWorkerViewCard: React.FC<FTWorkerViewCardProps> = ({
	startDateTime,
	endDateTime,
	shiftWorkcenter,
	shiftPrimaryJob,
	isOverTimeShift,
  isSamePrimaryJob,
  isSameWorkcenter = false,
  isSameShiftPattern = true,
	isFlexShift,
	schedulingStatus = "SCH",
	previousSchedulingStatus,
	workerId,
	shiftId,
	workerFullName,
	shiftPatternName,
	workerJobWorkcenter,
	isAttendanceManagementEnabled,
  workerPrimaryWorkcenterName,
  workerPrimaryJobName,
  isPrinting = false,
	skillIds = null,
  isMoved = false,
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const chipRef = useRef<HTMLDivElement>(null);
	const open = Boolean(anchorEl);
	const { t } = useTranslation();

	const {
		filterData: { allWorkcentersObj, allJobsObj, schedulingStatusesObj, allSkills },
	} = useWorkerSchedulerData();

	const shiftWorkcenterName = allWorkcentersObj[shiftWorkcenter]?.name;
	const shiftPrimaryJobName = allJobsObj[shiftPrimaryJob]?.name;

	const dispatch = useDispatch();

	// GAT-7252 - without the key the chip still shows the status but opens
	// no menu, same as the status chip in the Shifts tab dialog.
	const permissions = useSelector(
		(state: StoreType) => state.user?.userData?.permissions
	);
	const canChangeStatus = hasPermissionString(
		permissions,
		FULL_TIME_SCHEDULER_PERMISSIONS.CHANGE_STATUS
	);

	const handleChipClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(chipRef.current);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleTransitionRequest = (toStatus: string) => {
		const transition = getTransition(schedulingStatus, toStatus);

		if (!transition) {
			console.warn(
				`Invalid transition from ${schedulingStatus} to ${toStatus}`
			);
			return;
		}

		const shiftDetails = {
			shiftPatternName,
			startDateTime,
			endDateTime,
		};

		const currentAssignment = {
			job: shiftPrimaryJobName,
			workcenter: shiftWorkcenterName,
			jobId: shiftPrimaryJob,
			workcenterId: shiftWorkcenter,
			jobWorkcenter: workerJobWorkcenter,
		};

		// Dispatch the dialog opening action - dialog will be rendered at app level
		dispatch(
			openTransitionDialog({
				dialogType: transition.dialogType,
				data: {
					transition,
					workerId,
					shiftId,
					toStatus,
					workerFullName,
					shiftDetails,
					currentAssignment,
          workerPrimaryWorkcenterName,
          workerPrimaryJobName,
					previousStatus: previousSchedulingStatus,
				},
			})
		);

		handleClose();
	};

	// Get filtered status options based on current status and transition rules
	const filteredStatusOptions = useFilteredStatusOptions(
		schedulingStatus,
		previousSchedulingStatus,
		handleTransitionRequest
	);

  // In moved place have status SCH instead of RSA
  if (schedulingStatus === "RAS" && isPrinting && isMoved) {
    schedulingStatus = "SCH";
  }

  // TODO: handling SCH status on pattern level change
  if (schedulingStatus === "SCH" && isPrinting && !isSameShiftPattern && !isMoved) {
    schedulingStatus = "RAS";
  }

  // If schedulingStatus is "COF" then do not display job and workcenter names
  const doNotDisplayJobAndWorkcenterNames = isPrinting;

  const getSkillNamesByIds = (allSkills:any, skillIdsString: string | null): string[] => {

	const skillIds: number[] = skillIdsString ? skillIdsString.split(",").map(id => parseInt(id.trim(), 10)) : [];

	const skillMap = new Map<number, string>();
	for (const skill of allSkills) {
		skillMap.set(skill.id, skill.name);
	}

	const skillNames: string[] = skillIds
	.map(id => skillMap.get(id)).filter((name): name is string => name !== undefined);

	return skillNames;
  };

  const skills = getSkillNamesByIds(allSkills, skillIds);

  const getDisplayName = () => {
    // When printing, always show all skills in a simple comma-separated list.
    if (isPrinting) {
        return skills.length > 0 ? skills.join(", ") : shiftPrimaryJobName;
    }

    // For the standard view (not printing)
    if (skills.length > 1) {
        // If there's more than one skill, show the first and a tooltip for the rest.
        return (
            <>
                {`${skills[0]}, `}
                <Tooltip
                    title={skills.slice(1).join(", ")}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "inherit",
                            color: "inherit",
                            textDecoration: "underline",
                            cursor: "pointer",
                        }}
                    >
                        {t("full-time-worker-scheduler_more-skills", {
                          num: skills.length - 1,
                        })}
                    </Typography>
                </Tooltip>
            </>
        );
    }

    // If there is exactly one or zero skills, display the name directly.
    return skills.length > 0 ? skills[0] : shiftPrimaryJobName;
};


	return (
		<Paper
			sx={{
				backgroundColor: isOverTimeShift ? "#EEEEEE" : "#FFFFFF",
				borderRadius: "8px",
				padding: isPrinting ? "2px 4px" : "8px",
				//border: "1px solid #CAC4D0",
				borderLeft: isPrinting ? 0 : isFlexShift ? "5px solid #DCEFF9" : `5px solid ${schedulingStatusesObj[schedulingStatus]?.borderColor}`,
				margin: isPrinting ? "2px 4px" : "8px",
        display: "flex",
        flexDirection: "column",
        width: isPrinting ? "calc(100% - 8px)" : "calc(100% - 16px)",
			}}
			elevation={isPrinting ? 0 : 1}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
          maxWidth: "100%",
          width: "100%",
				}}
			>
        {!isPrinting &&
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              maxWidth: "100%",
              width: "100%",
              gap: isPrinting ? "2px" : "4px",
            }}
          >
            {!isSameShiftPattern &&
              <ScheduleOutlined sx={{color: "#000000DE",
                fontSize: isPrinting ? "12px" : "15px",
                marginTop: isPrinting ? "1px" : "2px",}}/>
            }
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.87)",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: isPrinting ? "16px" : "20px",
                fontSize: isPrinting ? "11px" : "13px",
              }}
            >
              {formatDate({
                dateStr: startDateTime,
                endDateStr: endDateTime,
                formatType: "TIME_RANGE",
              })}
            </Typography>
          </Box>
        }
        {!doNotDisplayJobAndWorkcenterNames && (
          <>
            {!isSameShiftPattern && (
              <Box sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "row",
                marginTop: isPrinting ? "2px" : "4px",
                maxWidth: "100%",
                width: "100%",
                gap: isPrinting ? "2px" : "4px",
              }}>
                <Group sx={{
                  color: "#000000DE",
                  fontSize: isPrinting ? "12px" : "14px",
                  marginTop: isPrinting ? "0px" : "2px",
                }} />
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: isPrinting ? "10px" : "12px",
                    lineHeight: isPrinting ? "14px" : "20px",
                    letterSpacing: "0.4px",
                    color: "#000000DE",
                    whiteSpace: "wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {shiftPatternName}
                </Typography>
              </Box>
            )}
            <Box sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "row",
              marginTop: isPrinting ? "2px" : "4px",
              maxWidth: "100%",
              width: "100%",
              gap: isPrinting ? "2px" : "4px",
            }}>
               {!isSamePrimaryJob && (shiftPrimaryJobName || skills.length > 0) && (
                 skills.length > 0 ?
                 <BuildOutlined sx={{
                   color: "#000000DE",
                   fontSize: isPrinting ? "12px" : "14px",
                   marginTop: isPrinting ? "0px" : "2px",
                 }} />
               : <WorkHistory sx={{
                 color: "#000000DE",
                 fontSize: isPrinting ? "12px" : "14px",
                 marginTop: isPrinting ? "0px" : "2px",
               }} />)}
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: isPrinting ? "10px" : "12px",
                  lineHeight: isPrinting ? "14px" : "20px",
                  letterSpacing: "0.4px",
                  color: "#000000DE",
                  whiteSpace: "wrap",
                  wordBreak: "break-word",
                }}
              >
                {getDisplayName()}
              </Typography>
            </Box>
            <Box sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "row",
              marginTop: isPrinting ? "2px" : "4px",
              maxWidth: "100%",
              width: "100%",
              gap: isPrinting ? "2px" : "4px",
            }}>
               {!isSameWorkcenter && <FactoryOutlined sx={{
                 color: "rgba(0, 0, 0, 0.60)",
                 fontSize: isPrinting ? "12px" : "14px",
                 marginTop: isPrinting ? "0px" : "2px",
               }} />}
              <Typography
                sx={{
                  color: "rgba(0, 0, 0, 0.60)",
                  fontSize: isPrinting ? "10px" : "12px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: isPrinting ? "14px" : "20px",
                  letterSpacing: "0.4px",
                  fontFamily: "Roboto",
                  whiteSpace: "wrap",
                  wordBreak: "break-word",
                }}
              >
                {shiftWorkcenterName}
              </Typography>
            </Box>
          </>
        )}
			</Box>
			{
				isFlexShift && (
					<Box>
						<Chip 
							sx={{
								backgroundColor: "#DCEFF9",
								"& .MuiChip-label": {
									color: isPrinting ? "rgba(0,0,0,0.87)" : "#0D47A1",
									fontFamily: "Roboto",
                  fontSize: isPrinting ? "10px" : "12px",
									fontStyle: "normal",
									fontWeight: 400,
									lineHeight: isPrinting ? "14px" : "20px",
									letterSpacing: "0.16px",
								},
							}}
							size="small"
							label={t("full-time-worker-scheduler_overtime")}
						/>
					</Box>
				)
			}
			{isAttendanceManagementEnabled && !isFlexShift && (
				<Box sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: isPrinting ? "center" : "flex-start",
        }}>
					<Chip
						ref = {chipRef}
						label={
							isPrinting
								? schedulingStatusesObj[schedulingStatus]?.code === "COF"
									? t("full-time-worker-scheduler_time-off-abbr")
									: schedulingStatusesObj[schedulingStatus]?.code
								: schedulingStatusesObj[schedulingStatus]?.name === "Called Off"
								? t("full-time-worker-scheduler_time-off")
								: schedulingStatusesObj[schedulingStatus]?.name
						}
						size='small'
						deleteIcon={isPrinting ? <></> : <KeyboardArrowDownOutlinedIcon />}
						onDelete={canChangeStatus ? handleChipClick : undefined}
						onClick={canChangeStatus ? handleChipClick : undefined}
						sx={{
							backgroundColor:
								schedulingStatusesObj[schedulingStatus]?.backgroundColor,
							cursor: canChangeStatus ? "pointer" : "default",
							"& .MuiChip-label": {
								color: isPrinting ? "rgba(0,0,0,0.87)" : schedulingStatusesObj[schedulingStatus]?.textColor,
								fontFamily: "Roboto",
                fontSize: "12px",
								fontStyle: "normal",
								fontWeight: 400,
								lineHeight: isPrinting ? "16px" : "20px",
								letterSpacing: "0.16px",
                padding: isPrinting ? "0px 4px" : "auto",
							},
						}}
						disabled={!isPrinting && isPast(new Date(endDateTime))}
					/>
					<Popover
						open={open}
						anchorEl={anchorEl}
						onClose={handleClose}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "left",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "left",
						}}
					>
						<Paper sx={{ minWidth: 120 }}>
							<MenuList>
								{filteredStatusOptions.map((option) => (
									<MenuItem key={option.id} onClick={option.onSelect}>
										<ListItemText primary={option.menuLabel || option.name} />
									</MenuItem>
								))}
							</MenuList>
						</Paper>
					</Popover>
				</Box>
			)}
		</Paper>
	);
};

export default FTWorkerViewCard;
